import { canAccess } from "../providers/commons/canAccess";
import { baseRole } from "../sales/roleProfiles";
import { projectSurfaceStyle } from "../deals/projectColors";
import { useQuery } from "@tanstack/react-query";
import { useGetIdentity, useDataProvider, useGetList } from "ra-core";
import { Link } from "react-router";
import { effectiveRole, isFullAccessRole } from "../sales/roles";
import { listWork, getWork } from "../workProjects/repository";
import { phaseLabels } from "../workProjects/model";
import type { CrmDataProvider } from "../providers/types";
import type { Task, Contact } from "../types";
import { format } from "date-fns";
const box = "rounded-3xl border bg-card p-5 space-y-4";
export function WorkspaceWidget() {
  const { identity } = useGetIdentity();
  const role = effectiveRole(identity);
  return isFullAccessRole(role) ||
    canAccess(role, { resource: "work_projects", action: "list" }) ? (
    <ProjectWidget />
  ) : canAccess(role, { resource: "contacts", action: "list" }) ? (
    <ContactWidget />
  ) : canAccess(role, { resource: "tasks", action: "list" }) ? (
    <TaskWidget />
  ) : (
    <aside className={box}>
      <h2 className="font-semibold">Рабочая область</h2>
      <p className="text-sm text-muted-foreground">
        Доступные разделы находятся в меню.
      </p>
    </aside>
  );
}
function ProjectWidget() {
  const { identity } = useGetIdentity();
  const provider = useDataProvider<CrmDataProvider>();
  const {
    data = [],
    isPending,
    error,
  } = useQuery({
    queryKey: ["home-work-projects", identity?.id],
    queryFn: () => listWork(provider),
    enabled: !!identity,
    meta: { persist: false },
    refetchInterval: 30000,
  });
  const change = useQuery({
    queryKey: ["home-project-change", identity?.id, data[0]?.id],
    queryFn: () => getWork(provider, data[0].id),
    enabled: !!data[0] && !identity?.administrator,
    meta: { persist: false },
    refetchInterval: 30000,
  });
  return (
    <aside aria-label="Проекты в работе" className={box}>
      <h2 className="font-semibold">
        {identity?.administrator ? "Проекты в работе" : "Мои проекты"}
      </h2>
      {error ? (
        <p>Не удалось загрузить проекты</p>
      ) : isPending ? (
        <p>Загрузка…</p>
      ) : !data.length ? (
        <p className="text-sm text-muted-foreground">
          Назначенных проектов пока нет
        </p>
      ) : (
        data.slice(0, 5).map((p) => {
          const step = p.state.steps.find((s) => s.status !== "approved");
          const completed = p.state.steps.filter(
            (s) => s.status === "approved",
          ).length;
          return (
            <Link
              key={p.id}
              style={projectSurfaceStyle(p.calendar_color)}
              data-project-color={p.calendar_color}
              to={`/work-projects/${p.id}`}
              className="block rounded-2xl border p-3 hover:bg-muted"
            >
              <p className="text-sm font-semibold break-words">
                №{p.id} · {p.name}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                {step ? phaseLabels[step.phase] : "Завершён"} · {completed}/
                {p.state.steps.length}
              </p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-[var(--brand-orange)]"
                  style={{
                    width: `${(completed / p.state.steps.length) * 100}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-xs">
                {step?.status === "pending"
                  ? "Ожидает приёмки"
                  : (step?.title ?? "Работы приняты")}
              </p>
              {p.deadline && (
                <p className="mt-1 text-xs text-muted-foreground">
                  До {format(new Date(p.deadline), "dd.MM.yyyy")}
                </p>
              )}
            </Link>
          );
        })
      )}
      {change.data?.state.events.at(-1) && (
        <div className="rounded-2xl bg-muted p-3 text-sm">
          <p className="mb-1 font-semibold">Последнее изменение</p>
          <p className="break-words">{change.data.state.events.at(-1)?.text}</p>
        </div>
      )}
      <Link
        className="inline-block text-sm text-primary underline"
        to="/work-projects"
      >
        Открыть проекты
      </Link>
    </aside>
  );
}
function TaskWidget() {
  const { identity } = useGetIdentity();
  const role = effectiveRole(identity);
  const {
    data = [],
    isPending,
    error,
  } = useGetList<Task>(
    "tasks",
    {
      filter: { sales_id: identity?.id, "done_date@is": null },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: 5 },
    },
    { enabled: !!identity },
  );
  const labelRole = baseRole(role);
  const title =
    labelRole === "accountant"
      ? "Оплаты и документы"
      : labelRole === "logistics"
        ? "Ближайшие доставки"
        : labelRole === "lead_installer"
          ? "Подготовка к монтажу"
          : "Мои поручения";
  return (
    <aside className={box}>
      <h2 className="font-semibold">{title}</h2>
      {isPending ? (
        <p>Загрузка…</p>
      ) : error ? (
        <p>Не удалось загрузить поручения</p>
      ) : !data.length ? (
        <p className="text-sm text-muted-foreground">Открытых поручений нет</p>
      ) : (
        data.map((t) => (
          <Link
            key={t.id}
            to={`/tasks?task=${t.id}`}
            className="block rounded-2xl border p-3 text-sm hover:bg-muted"
          >
            <p className="font-medium break-words">{t.text}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {format(new Date(t.due_date), "dd.MM · HH:mm")}
            </p>
          </Link>
        ))
      )}
    </aside>
  );
}
function ContactWidget() {
  const { identity } = useGetIdentity();
  const { data = [], isPending } = useGetList<Contact>(
    "contacts_summary",
    {
      filter: { sales_id: identity?.id },
      sort: { field: "last_seen", order: "DESC" },
      pagination: { page: 1, perPage: 5 },
    },
    { enabled: !!identity },
  );
  return (
    <aside className={box}>
      <h2 className="font-semibold">Контрагенты в работе</h2>
      {isPending ? (
        <p>Загрузка…</p>
      ) : (
        data.map((c) => (
          <Link
            key={c.id}
            to={`/contacts/${c.id}/show`}
            className="block rounded-xl border p-3 text-sm hover:bg-muted"
          >
            {c.first_name} {c.last_name}
            <p className="mt-1 text-xs text-muted-foreground">
              {c.company_name}
            </p>
          </Link>
        ))
      )}
    </aside>
  );
}
